import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const steps = [
  {
    number: '01',
    icon: 'ri-message-3-line',
    title: 'Consultation',
    description:
      'Call us or submit a quote request. We discuss your vehicle, property, or vessel, understand your goals, and recommend the perfect film type and shade for your needs.',
    detail: 'Free · No pressure · Same-day response',
  },
  {
    number: '02',
    icon: 'ri-file-list-3-line',
    title: 'Custom Plan',
    description:
      'We prepare a detailed, no-obligation proposal including film selection, coverage areas, pricing, and timeline — tailored to your exact specifications.',
    detail: 'Itemized quote · Multiple options',
  },
  {
    number: '03',
    icon: 'ri-tools-line',
    title: 'Expert Installation',
    description:
      'Our certified technicians perform a precise installation in our fully equipped facility. Every edge is sealed, every surface cleaned — zero bubbles, zero compromises.',
    detail: 'Certified team · Controlled environment',
  },
  {
    number: '04',
    icon: 'ri-customer-service-2-line',
    title: 'Follow-Up Support',
    description:
      "We conduct a final quality inspection with you and provide aftercare guidance. Our lifetime warranty means we're always here if you ever need us.",
    detail: 'Lifetime warranty · Ongoing support',
  },
];

export function Process() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="process" className="py-28 bg-[#F7F5F2] relative overflow-hidden">
      {/* Subtle noise */}
      <div className="absolute inset-0 opacity-[0.02]"
        style={{ backgroundImage: 'radial-gradient(circle, #0A0A0A 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={ref}>
        {/* Header */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-20`}>
          <div>
            <span className="luxury-divider" />
            <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">
              Our Process
            </span>
            <h2
              className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] leading-tight tracking-tight"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              From First Call
              <br />
              <span className="text-[#FF6B35]">to Flawless Finish</span>
            </h2>
          </div>
          <p className="text-[#0A0A0A]/50 text-base leading-relaxed">
            Our streamlined process is designed to deliver a premium experience — from the moment you contact us to the day you drive away with perfectly tinted windows.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`anim-hidden ${isVisible ? `animate-fade-in-up delay-${(i + 1) * 100}` : ''} group relative bg-[#0A0A0A] rounded-3xl overflow-hidden cursor-default`}
            >
              {/* Corner number watermark */}
              <div
                className="absolute -top-4 -right-2 text-[7rem] font-black text-white/[0.035] leading-none select-none pointer-events-none"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {step.number}
              </div>

              {/* Top gradient line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF6B35]/50 to-transparent group-hover:via-[#C9A96E] transition-all duration-500" />

              <div className="relative z-10 p-8 flex flex-col h-full min-h-[280px]">
                {/* Icon block */}
                <div className="flex items-center gap-4 mb-7">
                  <div className="relative">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FF6B35] to-[#C9A96E]/80 rounded-2xl shadow-lg group-hover:scale-105 transition-transform duration-300">
                      <i className={`${step.icon} text-xl text-white`} />
                    </div>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
                  <span className="text-xs font-black text-white/15 tracking-widest" style={{ fontFamily: 'Sora, sans-serif' }}>
                    STEP {i + 1}
                  </span>
                </div>

                {/* Title */}
                <h3
                  className="text-lg font-bold text-white mb-3 tracking-tight"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-white/45 leading-relaxed flex-1">{step.description}</p>

                {/* Bottom badge */}
                <div className="mt-6 pt-5 border-t border-white/5">
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#C9A96E]/70 font-medium">
                    <i className="ri-checkbox-circle-line text-[#FF6B35] text-sm" />
                    {step.detail}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`anim-hidden ${isVisible ? 'animate-fade-in-up delay-500' : ''} mt-12 bg-[#0A0A0A] rounded-3xl p-10 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-8 relative overflow-hidden`}
        >
          {/* Subtle radial */}
          <div className="absolute right-0 top-0 bottom-0 w-80 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at right center, rgba(255,107,53,0.08) 0%, transparent 70%)' }}
          />
          <div className="relative z-10">
            <h3
              className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Ready to Get Started?
            </h3>
            <p className="text-white/40 text-sm">
              Most automotive jobs completed same-day. Residential &amp; marine by appointment.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 relative z-10 flex-shrink-0">
            <a
              href="tel:+15612759473"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-white/5 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-[#FF6B35]" />
              (561) 275-9473
            </a>
            <a
              href="#quote"
              className="inline-flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-7 py-3.5 rounded-full text-sm font-bold transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Book My Appointment <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
